import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty, isFunction } from 'lodash';
import { ArrowDropDownIcon } from 'tuui';
import {
    Divider,
    DropdownButton,
    DropdownMenu,
    STYLED_BUTTON_SIZE,
    StyledButton,
    withStylesheet,
} from 'taboola-ultimate-ui';
import { useDimension, useDropdownOptions, useModuleName, useNavigate } from 'hooks';
import { INITIAL_FORM_ACCOUNT_NAME } from 'hooks/queryParams/useInitialFormAccountName';
import useAvailableItems from 'hooks/useAvailableItems';
import { CreateAccountButton } from 'modules/accounts/components/CreateAccountButton/CreateAccountButton';
import { MODULE_NAME as ACCOUNTS_MODULE_NAME } from 'modules/accounts/config/routes/module';
import CreateAudienceButton from 'modules/audiences/components/CreateAudienceButton/CreateAudienceButton';
import { MODULE_NAME as AUDIENCES_MODULE_NAME } from 'modules/audiences/config/routes';
import { useShowCampaignGroupTooltip } from 'modules/campaigns/hooks';
import { isCreativeLibraryEnabled } from 'modules/campaigns/modules/campaigns-reports/config/predicates';
import {
    generateBulkCampaignCreatorPath,
    generateCampaignCreatorPath,
    generateCampaignsGroupCreatorPath,
    generateCreativeCreatorPath,
    generateVideoPMPCreatorPath,
} from 'modules/campaigns/services/utils';
import { CreateLibraryCreativeButton } from 'modules/creative-library/components/CreateLibraryCreativeButton/CreateLibraryCreativeButton';
import { MODULE_NAME as CREATIVE_LIBRARY_MODULE } from 'modules/creative-library/config/routes';
import { CreateLandingPageButton } from 'modules/landing-pages/components/CreateButton/CreateButton';
import { MODULE_NAME as LANDING_PAGES_MODULE } from 'modules/landing-pages/config/routes';
import { CreatePerformanceRulesButton } from 'modules/performance-rules/components/CreatePerformanceRulesButton/CreatePerformanceRulesButton';
import { MODULE_NAME as PERFORMANCE_RULES_MODULE } from 'modules/performance-rules/config/routes';
import { CreatePushApiButton } from 'modules/push-api/components/CreatePushApiButton/CreatePushApiButton';
import { MODULE_NAME as PUSH_API_MODULE } from 'modules/push-api/config/routes/module.js';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useAccountFullConfigData } from 'modules/taboola-common-frontend-modules/account-configurations/hooks/useAccountFullConfigData';
import { PermissionsGuard } from 'modules/taboola-common-frontend-modules/authentication';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker/config';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { mergeQueryParamsWithLocation } from 'modules/taboola-common-frontend-modules/query-params';
import { CreateConversionButton } from 'modules/tracking/components/CreateConversionButton/CreateConversionButton';
import { MODULE_NAME as TRACKING_MODULE_NAME } from 'modules/tracking/config/routes';
import { selectedAccountSelector } from 'selectors';
import { isSponsoredCampaign, isVideoCampaign } from 'services/campaignDimension';
import {
    isNetworkAccountType,
    isOpenExchangeAccount,
    isPmpAccount,
    isProgrammaticAccount,
} from '../../../../../../account-management/accountType';
import { generateOnboardingFormPath } from '../../../../../../onboarding/config/generateOnboardingFormPath';
import { ONBOARDING_ENTITY } from '../../../../../../onboarding/config/onboardingEntity';
import { ONBOARDING_CHAT_DONE_KEY } from '../../../../../../onboarding/config/onboardingSkipKey';
import { useLocalStorage } from '../../../../../../taboola-common-frontend-modules/storage';
import { ENTITY, MODULE_NAME as CAMPAIGNS_MODULE_NAME } from '../../../../../config';
import DEMAND_TYPE from '../../../../../constants/demandType';
import { useOptionsWithTooltip } from '../../../../../hooks/useOptionsWithTooltip';
import { useSelectedCampaign } from '../../../../../hooks/useSelectedCampaign';
import { campaignsMapSelector } from '../../../../../selectors';
import styles from '../gridToolbar.module.scss';

const isFeatureFlagEnabled = (accountConfig, featureFlagEnabled) => accountConfig?.[featureFlagEnabled] === 'true';

const optionsConfigurations = [
    {
        id: 'campaigns-group',
        value: 'campaigns-group',
        label: 'Campaign Group',
        path: generateCampaignsGroupCreatorPath(),
        dataMetricColumnName: 'Campaign Group',
        permissions: 'EDIT_CAMPAIGN_GROUP',
        visibilityCondition: ({ selectedAccount, moduleName, selectedCampaignDimension, selectedAccountConfig }) =>
            !isProgrammaticAccount(selectedAccount) &&
            !isNetworkAccountType(selectedAccount.type) &&
            moduleName.includes(CAMPAIGNS_MODULE_NAME) &&
            !isVideoCampaign(selectedCampaignDimension) &&
            selectedAccountConfig[FEATURE_FLAGS.CAMPAIGN_GROUP_ENABLED] === 'true',
    },
    {
        id: 'campaign',
        value: 'campaign',
        label: 'Campaign',
        path: generateCampaignCreatorPath({ entity: ENTITY.CAMPAIGN }),
        dataMetricColumnName: 'Campaign',
        permissions: ['CAMPAIGN_EDIT', 'CAMPAIGN_ADD'],
        visibilityCondition: ({ selectedAccount, moduleName, selectedCampaignDimension }) =>
            !isProgrammaticAccount(selectedAccount) &&
            moduleName.includes(CAMPAIGNS_MODULE_NAME) &&
            isSponsoredCampaign(selectedCampaignDimension),
    },
    {
        id: 'campaign-onboarding-create-button',
        value: 'campaignOnboarding',
        label: 'Campaign (AI-Assistant)',
        onBeforeNavigate: ({ setLocalStorage }) => {
            setLocalStorage(ONBOARDING_CHAT_DONE_KEY, false);
        },
        path: generateOnboardingFormPath({ entity: ONBOARDING_ENTITY.ONBOARDING_CHAT }),
        dataMetricColumnName: 'Campaign (AI-Assistant)',
        permissions: ['CAMPAIGN_EDIT', 'CAMPAIGN_ADD', 'CHAT_AGENT_PERMISSION'],
        visibilityCondition: ({ selectedAccount, moduleName, selectedCampaignDimension, campaigns }) =>
            !isProgrammaticAccount(selectedAccount) &&
            moduleName.includes(CAMPAIGNS_MODULE_NAME) &&
            isSponsoredCampaign(selectedCampaignDimension) &&
            isEmpty(campaigns),
    },
    {
        id: 'creative',
        value: 'creative',
        label: 'Ads',
        path: generateCreativeCreatorPath({ entity: ENTITY.CREATIVE }),
        queryParamGetter: ({ selectedCampaign }) =>
            selectedCampaign ? { [INITIAL_FORM_ACCOUNT_NAME]: selectedCampaign.accountName } : {},
        dataMetricColumnName: 'Ads',
        permissions: ['CREATIVE_EDIT'],
        visibilityCondition: ({ selectedAccount, moduleName, selectedCampaignDimension, selectedAccountConfig }) =>
            !isProgrammaticAccount(selectedAccount) &&
            moduleName.includes(CAMPAIGNS_MODULE_NAME) &&
            isSponsoredCampaign(selectedCampaignDimension) &&
            !isCreativeLibraryEnabled(selectedAccountConfig),
    },
    {
        id: 'excel-bulk-upload',
        value: 'excel-bulk-upload',
        label: 'Bulk Upload',
        path: generateBulkCampaignCreatorPath({ entity: ENTITY.EXCEL_BULK }),
        dataMetricColumnName: 'Bulk Upload',
        permissions: ['CAMPAIGN_EDIT', 'CAMPAIGN_ADD'],
        visibilityCondition: ({
            selectedAccount,
            moduleName,
            selectedCampaignDimension,
            accountConfig,
            permissionsMap,
        }) => {
            const isBulkUploadEnabled =
                isFeatureFlagEnabled(accountConfig, FEATURE_FLAGS.CAMPAIGN_BULK_UPLOAD) ||
                permissionsMap['ADS_CONSOLE_BULK_UPLOAD_USER_ENABLED'];

            return (
                isBulkUploadEnabled &&
                !isProgrammaticAccount(selectedAccount) &&
                moduleName.includes(CAMPAIGNS_MODULE_NAME) &&
                isSponsoredCampaign(selectedCampaignDimension)
            );
        },
        hasDividerBefore: true,
    },
    {
        id: 'video-campaign',
        value: 'video-campaign',
        label: 'Video Campaign',
        path: generateCampaignCreatorPath({ entity: ENTITY.CAMPAIGN_VIDEO }),
        dataMetricColumnName: 'Video Campaign',
        permissions: 'ADS_CONSOLE_CREATE_VIDEO_CAMPAIGN',
        visibilityCondition: ({ selectedAccount, moduleName, selectedCampaignDimension }) =>
            !isProgrammaticAccount(selectedAccount) &&
            moduleName.includes(CAMPAIGNS_MODULE_NAME) &&
            isVideoCampaign(selectedCampaignDimension),
    },
    {
        id: 'video-creative',
        value: 'video-creative',
        label: 'Video Ads',
        path: generateCreativeCreatorPath({ entity: ENTITY.CREATIVE_VIDEO }),
        dataMetricColumnName: 'Video Ads',
        permissions: 'ADS_CONSOLE_CREATE_VIDEO_CAMPAIGN',
        visibilityCondition: ({ selectedAccount, moduleName, selectedCampaignDimension }) =>
            !isProgrammaticAccount(selectedAccount) &&
            moduleName.includes(CAMPAIGNS_MODULE_NAME) &&
            isVideoCampaign(selectedCampaignDimension),
    },
    {
        id: DEMAND_TYPE.RTB_OPEN_EXCHANGE,
        value: 'open-exchange',
        label: 'Open Exchange Campaign',
        path: generateCampaignCreatorPath({ entity: ENTITY.RTB_OPEN_EXCHANGE }),
        dataMetricColumnName: 'Open Exchange Campaign',
        permissions: ['CAMPAIGN_EDIT', 'CAMPAIGN_ADD'],
        visibilityCondition: ({ selectedAccount, moduleName, selectedCampaignDimension }) =>
            isOpenExchangeAccount(selectedAccount) &&
            moduleName.includes(CAMPAIGNS_MODULE_NAME) &&
            !isVideoCampaign(selectedCampaignDimension),
    },
    {
        id: DEMAND_TYPE.PMP_DEAL,
        value: 'pmp-deal',
        label: 'PMP Deal',
        path: generateCampaignCreatorPath({ entity: ENTITY.PMP_DEAL }),
        dataMetricColumnName: 'PMP Deal',
        permissions: ['DEX_UI_ADVERTISERS'],
        visibilityCondition: ({ selectedAccount, moduleName, selectedCampaignDimension }) =>
            isPmpAccount(selectedAccount) &&
            moduleName.includes(CAMPAIGNS_MODULE_NAME) &&
            !isVideoCampaign(selectedCampaignDimension),
    },
    {
        id: 'video-pmp-deal',
        value: 'video-pmp-deal',
        label: 'Video Campaign PMP',
        path: generateVideoPMPCreatorPath(),
        dataMetricColumnName: 'Video PMP Campaign',
        permissions: 'ADS_CONSOLE_CREATE_VIDEO_CAMPAIGN',
        visibilityCondition: ({ selectedAccount, moduleName, selectedCampaignDimension }) =>
            isPmpAccount(selectedAccount) &&
            moduleName.includes(CAMPAIGNS_MODULE_NAME) &&
            isVideoCampaign(selectedCampaignDimension),
    },
];

const OptionWrapper = ({ children, dataMetricColumnName, permissions, hasDividerBefore, id }) => (
    <PermissionsGuard permissions={permissions}>
        <div data-metrics-column-name={dataMetricColumnName} id={id}>
            {hasDividerBefore && <Divider className={styles['divider']} />}
            {children}
        </div>
    </PermissionsGuard>
);

const StyledAddButtonNewShape = withStylesheet(StyledButton, styles, {
    [StyledButton.classNames.main]: ['tbl-styled-btn', 'override-btn-style'],
    [StyledButton.classNames.iconBefore]: 'dropdown-button-icon',
});

const CreateButton = () => {
    const moduleName = useModuleName();
    const config = useAccountFullConfigData();
    const selectedAccount = useSelector(selectedAccountSelector);
    const campaigns = useSelector(campaignsMapSelector);
    const { campaign: selectedCampaign } = useSelectedCampaign();
    const [selectedCampaignDimension] = useDimension();
    const { isActive: isActiveAccount } = selectedAccount;
    const isDisabled = useConfigMatch({ [FEATURE_FLAGS.DISABLE_CREATE_BUTTON]: 'true' });
    const [, setLocalStorage] = useLocalStorage();
    const shouldShowTooltip = useShowCampaignGroupTooltip();

    const rawOptions = useAvailableItems(optionsConfigurations, {
        selectedAccount,
        moduleName,
        selectedCampaignDimension,
        campaigns,
        ...config,
    });

    const translatedOptions = useDropdownOptions(rawOptions, 'app.gridToolbar');
    const options = useOptionsWithTooltip(translatedOptions);

    const navigate = useNavigate();

    const handleOnChange = useCallback(
        (option, { closeDropdown } = {}) => {
            const { onBeforeNavigate, path, queryParamGetter = () => ({}), value } = option;

            if (shouldShowTooltip && (value === 'campaign' || value === 'campaignOnboarding')) {
                return;
            }

            if (isFunction(onBeforeNavigate)) {
                onBeforeNavigate({ setLocalStorage });
            }
            const pathWithParams = mergeQueryParamsWithLocation(
                { pathname: path },
                queryParamGetter({ selectedCampaign })
            );
            navigate(pathWithParams);

            if (closeDropdown) {
                closeDropdown();
            }
        },
        [selectedCampaign, navigate, setLocalStorage, shouldShowTooltip]
    );

    if (moduleName.includes(ACCOUNTS_MODULE_NAME)) {
        return <CreateAccountButton />;
    }
    if (moduleName.includes(AUDIENCES_MODULE_NAME)) {
        return <CreateAudienceButton />;
    }
    if (moduleName.includes(TRACKING_MODULE_NAME)) {
        return <CreateConversionButton />;
    }
    if (moduleName.includes(PERFORMANCE_RULES_MODULE)) {
        return <CreatePerformanceRulesButton />;
    }
    if (moduleName.includes(PUSH_API_MODULE)) {
        return <CreatePushApiButton />;
    }
    if (moduleName.includes(CREATIVE_LIBRARY_MODULE)) {
        return <CreateLibraryCreativeButton />;
    }
    if (moduleName.includes(LANDING_PAGES_MODULE)) {
        return <CreateLandingPageButton />;
    }

    if (isEmpty(rawOptions) || !isActiveAccount) {
        return null;
    }

    const buttonText = (
        <>
            <FormattedMessage id="app.gridToolbar.new" defaultMessage="<b>New</b>" />
            <div className={styles['delimiter']} />
        </>
    );

    return (
        <div
            data-metrics-event-name={GTM_EVENTS.USABILITY}
            data-metrics-component="CreateButton"
            data-automation="CreateButton"
        >
            <DropdownButton
                ButtonComponent={StyledAddButtonNewShape}
                buttonText={buttonText}
                onChange={handleOnChange}
                buttonSize={STYLED_BUTTON_SIZE.MEDIUM}
                options={options}
                disabled={isDisabled}
                optionWrapper={OptionWrapper}
                shouldStretchedByContent
                strechedMenuPosition={DropdownMenu.MENU_POSITION.RIGHT}
                buttonIconAfter={<ArrowDropDownIcon style={{ fontSize: 'x-large' }} />}
                preventAutoClose={shouldShowTooltip}
                dropdownStyles={{
                    menu: {
                        borderRadius: styles.dropdownBorderRadius,
                        border: styles.dropdownBorder,
                        boxShadow: styles.dropdownBoxShadow,
                    },
                }}
            />
        </div>
    );
};

export default CreateButton;
